/**
 * @fileoverview File Upload Module Types and Configuration
 * 
 * Type definitions and validation for the File Upload Module
 */

/**
 * @typedef {Object} FileUploadConfig
 * @property {'user-kb'|'user-kb-dashboard'|'user-kb-files'|'parrot-config'|'admin-kb'} context - Upload context
 * @property {'user'|'admin'} securityLevel - Security level for validation
 * @property {string[]} allowedTypes - Allowed file extensions (without dots)
 * @property {number} maxFileSize - Maximum file size in bytes
 * @property {boolean} multiple - Allow multiple file selection
 * @property {boolean} showProgress - Show upload progress
 * @property {boolean} concurrent - Allow concurrent uploads
 * @property {string} uploadEndpoint - API endpoint for file upload
 * @property {boolean} triggerSync - Trigger sync after upload
 * @property {string} [syncEndpoint] - API endpoint for sync trigger (deprecated)
 * @property {string} [parrotId] - Parrot ID for ElevenLabs sync
 * @property {Object} [metadata] - Additional metadata to send with upload
 * @property {function} [onUploadComplete] - Callback when upload completes
 * @property {function} [onUploadProgress] - Callback for progress updates
 * @property {function} [onUploadError] - Callback for upload errors
 * @property {number} [maxConcurrentUploads] - Maximum concurrent uploads
 * @property {number} [retryAttempts] - Number of retry attempts for failed uploads
 * @property {number} [retryDelay] - Delay between retry attempts in ms
 */

/**
 * @typedef {Object} UploadFile
 * @property {string} id - Unique upload ID
 * @property {File} file - The actual file object
 * @property {'pending'|'uploading'|'completed'|'failed'} status - Upload status
 * @property {number} progress - Upload progress (0-100)
 * @property {number} [loaded] - Bytes loaded
 * @property {number} [total] - Total bytes
 * @property {Date} [startTime] - Upload start time
 * @property {Date} [endTime] - Upload end time
 * @property {string} [error] - Error message if failed
 * @property {*} [result] - Upload result if successful
 * @property {number} [estimatedTimeRemaining] - Estimated time remaining in ms
 */

/**
 * @typedef {Object} FileValidationResult
 * @property {File[]} validFiles - Files that passed validation
 * @property {Array<{file: File, errors: string[]}>} invalidFiles - Files that failed validation
 */

/**
 * @typedef {Object} SecurityValidationResult
 * @property {boolean} valid - Whether validation passed
 * @property {string[]} errors - Validation errors
 * @property {string[]} warnings - Validation warnings
 */

/**
 * @typedef {Object} UploadResult
 * @property {*[]} successful - Successfully uploaded files
 * @property {Array<{file: File, error: Error}>} failed - Failed uploads
 * @property {number} total - Total number of files processed
 */

/**
 * Default file upload configuration
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6
 */
export const DEFAULT_FILE_UPLOAD_CONFIG = {
  context: 'user-kb',
  securityLevel: 'user',
  allowedTypes: ['pdf', 'docx', 'txt', 'html'], // Strict validation: NO JSON/CSV per requirements 1.2, 1.3
  maxFileSize: 4 * 1024 * 1024, // 4MB per requirements 1.2, 1.5
  multiple: true, // Multiple file selection per requirements 1.1
  showProgress: true, // Progress tracking per requirements 1.4
  concurrent: false,
  uploadEndpoint: '/api/modules/file-upload',
  triggerSync: false,
  syncEndpoint: '/api/modules/file-upload/sync',
  maxConcurrentUploads: 3,
  retryAttempts: 2,
  retryDelay: 1000
};

/**
 * Context-specific configurations
 */
export const CONTEXT_CONFIGS = {
  'user-kb': {
    allowedTypes: ['pdf', 'docx', 'txt', 'html'],
    maxFileSize: 4 * 1024 * 1024, // 4MB
    uploadEndpoint: '/api/modules/file-upload',
    syncEndpoint: '/api/modules/file-upload/sync',
    securityLevel: 'user',
    triggerSync: false // Sync manually or when parrotId is provided
  },
  'user-kb-dashboard': {
    allowedTypes: ['pdf', 'docx', 'txt', 'html'],
    maxFileSize: 4 * 1024 * 1024, // 4MB
    uploadEndpoint: '/api/modules/file-upload',
    syncEndpoint: '/api/modules/file-upload/sync',
    securityLevel: 'user',
    triggerSync: false // Dashboard context - manual sync
  },
  'user-kb-files': {
    allowedTypes: ['pdf', 'docx', 'txt', 'html'],
    maxFileSize: 4 * 1024 * 1024, // 4MB
    uploadEndpoint: '/api/modules/file-upload',
    syncEndpoint: '/api/modules/file-upload/sync',
    securityLevel: 'user',
    triggerSync: false // File management context - manual sync
  },
  'parrot-config': {
    allowedTypes: ['pdf', 'docx', 'txt', 'html'], // Updated: NO JSON/CSV per requirements 1.2, 1.3
    maxFileSize: 4 * 1024 * 1024, // Updated: 4MB per requirements 1.2, 1.5
    uploadEndpoint: '/api/modules/file-upload',
    syncEndpoint: '/api/modules/file-upload/sync',
    securityLevel: 'user',
    triggerSync: true // Auto-sync for parrot configuration
  },
  'admin-kb': {
    allowedTypes: ['pdf', 'docx', 'txt', 'html'], // Updated: NO JSON/CSV per requirements 1.2, 1.3
    maxFileSize: 4 * 1024 * 1024, // Updated: 4MB per requirements 1.2, 1.5
    uploadEndpoint: '/api/modules/file-upload',
    syncEndpoint: '/api/modules/file-upload/sync',
    securityLevel: 'admin',
    triggerSync: false // Admin controls sync manually
  }
};

/**
 * File type configurations
 */
export const FILE_TYPE_CONFIG = {
  'pdf': {
    mimeTypes: ['application/pdf'],
    maxSize: 10 * 1024 * 1024, // 10MB
    description: 'PDF Document'
  },
  'docx': {
    mimeTypes: [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ],
    maxSize: 5 * 1024 * 1024, // 5MB
    description: 'Word Document'
  },
  'txt': {
    mimeTypes: ['text/plain'],
    maxSize: 4 * 1024 * 1024, // 4MB to match global limit
    description: 'Text File'
  },
  'html': {
    mimeTypes: ['text/html'],
    maxSize: 4 * 1024 * 1024, // 4MB to match global limit
    description: 'HTML File'
  },
  'json': {
    mimeTypes: ['application/json'],
    maxSize: 1 * 1024 * 1024, // 1MB
    description: 'JSON File'
  },
  'csv': {
    mimeTypes: ['text/csv', 'application/csv'],
    maxSize: 5 * 1024 * 1024, // 5MB
    description: 'CSV File'
  }
};

/**
 * Validate file upload configuration
 * 
 * @param {FileUploadConfig} config - Configuration to validate
 * @returns {{valid: boolean, errors: string[]}} Validation result
 */
export function validateFileUploadConfig(config) {
  const errors = [];

  // Check required fields
  if (!config) {
    errors.push('Configuration is required');
    return { valid: false, errors };
  }

  if (!config.context) {
    errors.push('Context is required');
  } else if (!['user-kb', 'parrot-config', 'admin-kb', 'user-kb-dashboard', 'user-kb-files'].includes(config.context)) {
    errors.push('Context must be one of: user-kb, parrot-config, admin-kb, user-kb-dashboard, user-kb-files');
  }

  if (!config.securityLevel) {
    errors.push('Security level is required');
  } else if (!['user', 'admin'].includes(config.securityLevel)) {
    errors.push('Security level must be either "user" or "admin"');
  }

  if (!config.allowedTypes || !Array.isArray(config.allowedTypes)) {
    errors.push('Allowed types must be an array');
  } else if (config.allowedTypes.length === 0) {
    errors.push('At least one allowed file type is required');
  } else {
    // Validate each file type
    const invalidTypes = config.allowedTypes.filter(
      type => !FILE_TYPE_CONFIG[type]
    );
    if (invalidTypes.length > 0) {
      errors.push(`Invalid file types: ${invalidTypes.join(', ')}`);
    }
    
    // Requirements 1.2, 1.3: Strict validation - NO JSON/CSV files allowed
    const forbiddenTypes = config.allowedTypes.filter(
      type => ['json', 'csv'].includes(type)
    );
    if (forbiddenTypes.length > 0) {
      errors.push(`Forbidden file types: ${forbiddenTypes.join(', ')}. Only PDF, DOCX, TXT, and HTML files are allowed per requirements 1.2 and 1.3`);
    }
  }

  if (typeof config.maxFileSize !== 'number' || config.maxFileSize <= 0) {
    errors.push('Max file size must be a positive number');
  }

  if (!config.uploadEndpoint || typeof config.uploadEndpoint !== 'string') {
    errors.push('Upload endpoint is required and must be a string');
  }

  if (typeof config.multiple !== 'boolean') {
    errors.push('Multiple must be a boolean');
  }

  if (typeof config.showProgress !== 'boolean') {
    errors.push('Show progress must be a boolean');
  }

  if (typeof config.concurrent !== 'boolean') {
    errors.push('Concurrent must be a boolean');
  }

  if (typeof config.triggerSync !== 'boolean') {
    errors.push('Trigger sync must be a boolean');
  }

  // Optional field validations
  if (config.syncEndpoint && typeof config.syncEndpoint !== 'string') {
    errors.push('Sync endpoint must be a string');
  }

  if (config.maxConcurrentUploads && 
      (typeof config.maxConcurrentUploads !== 'number' || config.maxConcurrentUploads <= 0)) {
    errors.push('Max concurrent uploads must be a positive number');
  }

  if (config.retryAttempts && 
      (typeof config.retryAttempts !== 'number' || config.retryAttempts < 0)) {
    errors.push('Retry attempts must be a non-negative number');
  }

  if (config.retryDelay && 
      (typeof config.retryDelay !== 'number' || config.retryDelay < 0)) {
    errors.push('Retry delay must be a non-negative number');
  }

  // Callback validations
  if (config.onUploadComplete && typeof config.onUploadComplete !== 'function') {
    errors.push('onUploadComplete must be a function');
  }

  if (config.onUploadProgress && typeof config.onUploadProgress !== 'function') {
    errors.push('onUploadProgress must be a function');
  }

  if (config.onUploadError && typeof config.onUploadError !== 'function') {
    errors.push('onUploadError must be a function');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Merge configuration with context-specific defaults
 * 
 * @param {FileUploadConfig} config - User configuration
 * @returns {FileUploadConfig} Merged configuration
 */
export function mergeWithContextDefaults(config) {
  const contextDefaults = CONTEXT_CONFIGS[config.context] || {};
  
  return {
    ...DEFAULT_FILE_UPLOAD_CONFIG,
    ...contextDefaults,
    ...config
  };
}

/**
 * Get file type information
 * 
 * @param {string} extension - File extension (without dot)
 * @returns {Object|null} File type configuration or null if not found
 */
export function getFileTypeInfo(extension) {
  return FILE_TYPE_CONFIG[extension] || null;
}

/**
 * Check if file type is allowed
 * 
 * @param {string} fileName - File name
 * @param {string[]} allowedTypes - Allowed file extensions
 * @returns {boolean} Whether file type is allowed
 */
export function isFileTypeAllowed(fileName, allowedTypes) {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension && allowedTypes.includes(extension);
}

/**
 * Check if file size is within limits
 * 
 * @param {number} fileSize - File size in bytes
 * @param {number} maxSize - Maximum allowed size in bytes
 * @returns {boolean} Whether file size is allowed
 */
export function isFileSizeAllowed(fileSize, maxSize) {
  return fileSize <= maxSize;
}

/**
 * Format file size for display
 * 
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Generate unique upload ID
 * 
 * @returns {string} Unique upload ID
 */
export function generateUploadId() {
  return `upload-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Create file upload configuration for specific context
 * 
 * @param {'user-kb'|'user-kb-dashboard'|'user-kb-files'|'parrot-config'|'admin-kb'} context - Upload context
 * @param {Partial<FileUploadConfig>} [overrides] - Configuration overrides
 * @returns {FileUploadConfig} Complete configuration
 */
export function createFileUploadConfig(context, overrides = {}) {
  const contextDefaults = CONTEXT_CONFIGS[context] || {};
  
  return {
    ...DEFAULT_FILE_UPLOAD_CONFIG,
    ...contextDefaults,
    context,
    ...overrides
  };
}

// Export the configuration class for JavaScript usage
export class FileUploadConfig {
  constructor(config = {}) {
    const merged = mergeWithContextDefaults(config);
    Object.assign(this, merged);
  }
  
  validate() {
    return validateFileUploadConfig(this);
  }
}
